import { z } from 'zod';
import { idSchema } from './common.js';
import { filterSchema, sortSchema } from './filters.js';
/**
 * Table state and saved views (RELAY-942). `tableStateSchema` is everything a list's UI can
 * change — which columns show, in what order, how wide, pinned left, the filter, the sort and
 * the free-text query — and it is what lives in the URL, in a saved view, and in the table
 * component's controlled state. One shape, three homes, so a saved view and a shared link are
 * the same thing.
 */
export const columnStateSchema = z.object({
    /** Column ids in display order; ids not listed keep their default order after these. */
    order: z.array(z.string().min(1).max(80)).max(200).default([]),
    hidden: z.array(z.string().min(1).max(80)).max(200).default([]),
    /** Column id → width in px. */
    sizes: z.record(z.string(), z.number().int().min(40).max(2000)).default({}),
    /** Column ids pinned to the left, in order. */
    pinned: z.array(z.string().min(1).max(80)).max(10).default([]),
});
export const tableStateSchema = z.object({
    columns: columnStateSchema.default({ order: [], hidden: [], sizes: {}, pinned: [] }),
    filter: filterSchema.optional(),
    sort: sortSchema.optional(),
    q: z.string().max(200).optional(),
    /**
     * Board views (RELAY-1038): which layout the list shows, and the fields a board card shows.
     * Both live in the same state as the filter, so a saved view or a link is the same list as
     * a table or as a board: switching layout never loses the filter, and vice versa.
     * `multiboard` (Projects only) stacks a board per pipeline, each foldable.
     */
    layout: z.enum(['table', 'board', 'multiboard']).optional(),
    card: z.object({ fields: z.array(z.string().min(1).max(80)).max(12) }).optional(),
    /**
     * Projects only (TS-1749): the pipeline the list and board show. Unset is the default
     * pipeline, listing every project (one that left it has no stage there). Kept with the
     * filter so a saved view such as "Premium Upsell" opens on that pipeline's board.
     */
    pipelineId: idSchema.optional(),
});
export const EMPTY_TABLE_STATE = {
    columns: { order: [], hidden: [], sizes: {}, pinned: [] },
};
/** The lists that keep saved views: the candidates table and the four boards' lists. */
export const SAVED_VIEW_ENTITIES = [
    'candidates',
    'projects',
    'deals',
    'jobs',
    'shortlist_candidates',
];
export const savedViewEntitySchema = z.enum(SAVED_VIEW_ENTITIES);
/** A personal saved view: the state above under a name, for one entity kind, owned by its creator. */
export const savedViewInputSchema = z.object({
    entity: savedViewEntitySchema,
    name: z.string().trim().min(1).max(100),
    state: tableStateSchema,
    /** Applied when the list opens with no state in the URL. At most one per user and entity. */
    isDefault: z.boolean().default(false),
});
export const savedViewSchema = savedViewInputSchema.extend({
    id: z.uuid(),
    userId: z.uuid(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
});
export const savedViewPatchSchema = savedViewInputSchema.omit({ entity: true }).partial();
/**
 * URL form of a table state: one `view` query parameter carrying base64url JSON, so a link
 * reproduces a list exactly and the DSL never has to be flattened into many parameters.
 * Garbage decodes to `undefined`, which callers treat as "no state in the URL".
 */
export function encodeTableState(state) {
    const compact = JSON.stringify(state);
    const bytes = new TextEncoder().encode(compact);
    let s = '';
    for (const b of bytes)
        s += String.fromCharCode(b);
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export function decodeTableState(encoded) {
    if (!encoded)
        return undefined;
    try {
        const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
        const bin = atob(b64);
        const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
        const parsed = tableStateSchema.safeParse(JSON.parse(new TextDecoder().decode(bytes)));
        return parsed.success ? parsed.data : undefined;
    }
    catch (err) {
        // A hand-edited or truncated link: treat as no state rather than crash the page.
        void err;
        return undefined;
    }
}
//# sourceMappingURL=views.js.map